import type { Epic } from '../../../index.js'
import { orderSlice } from '../../../Slice/Order/OrderSlice.js'
import { catchError, filter, forkJoin, mergeMap, of } from 'rxjs'
import { withLatestFrom } from 'rxjs/operators'
import type { NegativeControl, Sample } from '../../../../Model/index.js'
import { openModal } from '../../../Slice/ModalSlice/ModalSlice.js'
import { addMessage } from '../../../Slice/Application/ApplicationSlice.js'
import { errorMessage } from '../../../../Model/Message/IMessage.js'
import { trackSubmitBlocked } from '../../../../Services/Telemetry/index.js'

export const UpdatePrimersForSubmitEpic: Epic = (action$, state$, { [orderSlice.name]: order, OrderRepository }) =>
  action$.pipe(
    filter(order.updatePrimersForSubmitRequest.match),
    withLatestFrom(state$),
    mergeMap(([action]) => {
      if (action.payload.data.length === 0) {
        // If data is empty, directly open the SubmitOrder modal
        // - this can happen e.g. when the user imports already updated samples from another completed order
        return of(
          openModal({
            modalType: 'SubmitOrder',
            props: {
              primer: action.payload.primer,
              order: action.payload.order
            }
          })
        )
      }

      // 1. Group by poolId
      const groupedByPoolId = action.payload.data.reduce<Record<string, typeof action.payload.data>>(
        (acc, item) => {
          const poolId = item.poolId
          if (poolId) {
            if (!acc[poolId]) acc[poolId] = []
            acc[poolId].push(item)
          }
          return acc
        },
        {} as Record<string, (Sample | NegativeControl)[]>
      )

      // 2. Create an array of observables for each group
      const requests$ = Object.entries(groupedByPoolId).map(([poolId, samples]) => {
        return OrderRepository.updatePoolContent(action.payload.order.id, poolId, samples)
      })

      // 3. Run all requests in parallel and dispatch success when all are done
      return forkJoin(requests$).pipe(
        mergeMap((results) => {
          // The /contents PUT returns a null slot for any item it couldn't update (its contract is
          // List<PoolItemStoredView?> — e.g. a stale/mismatched poolId in the submit batch). Strip
          // those before they reach the reducer, which dereferences sample.poolId (SPCFY-FRONTEND-J1).
          const flattenedResults = results.flat().filter((item) => item != null)

          return of(
            order.updatePrimersForSubmitSuccess({ orderId: action.payload.order.id, result: flattenedResults }),
            openModal({
              modalType: 'SubmitOrder',
              props: {
                primer: action.payload.primer,
                order: action.payload.order
              }
            })
          )
        }),
        catchError((error) => {
          console.error('Batch update failed', error)
          trackSubmitBlocked('pool_update_failed', action.payload.order)
          // The widget has already told the user validation succeeded; without this the failed PUT
          // leaves them on an unchanged page with no modal and no explanation.
          return of(
            addMessage(errorMessage('The order could not be prepared for submission. Please try again.')),
            order.updatePrimersForSubmitFailure({ orderId: action.payload.order.id, error })
          )
        })
      )
    })
  )
